import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

const CountdownText = ({ startTime, allowedMinutes, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState("");

  // Calculate end time based on start time and allowed minutes
  const endTime = useMemo(() => {
    const startDate = new Date(startTime);
    return new Date(startDate.getTime() + allowedMinutes * 60 * 1000);
  }, [startTime, allowedMinutes]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const start = new Date(startTime).getTime();
      const end = endTime.getTime();

      // If current time is before start time, show full duration
      if (now < start) {
        const duration = end - start;
        return {
          timeString: formatDuration(duration),
          isComplete: false,
        };
      }

      // If current time is after end time, show 00:00
      if (now > end) {
        return {
          timeString: "00:00",
          isComplete: true,
        };
      }

      // Calculate remaining time
      const remaining = end - now;
      return {
        timeString: formatDuration(remaining),
        isComplete: false,
      };
    };

    const formatDuration = (duration) => {
      if (duration < 0) return "00:00";

      // Calculate hours, minutes, seconds
      const hours = Math.floor(duration / (1000 * 60 * 60));
      const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((duration % (1000 * 60)) / 1000);

      // Format with leading zeros
      const formatNumber = (num) => num.toString().padStart(2, "0");

      // If there are hours, show HH:MM
      if (hours > 0) {
        return `${formatNumber(hours)}:${formatNumber(minutes)}`;
      }

      // If no hours, show MM:SS
      return `${formatNumber(minutes)}:${formatNumber(seconds)}`;
    };

    // Initial calculation
    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft.timeString);
    if (initialTimeLeft.isComplete && onComplete) {
      onComplete();
    }

    // Update every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft.timeString);

      // If countdown is complete, clear interval and call onComplete
      if (newTimeLeft.isComplete) {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(timer);
  }, [startTime, endTime, onComplete]);

  // Calculate percentage of time elapsed
  const progressPercentage = useMemo(() => {
    const now = new Date().getTime();
    const start = new Date(startTime).getTime();
    const end = endTime.getTime();
    const total = end - start;
    const elapsed = now - start;

    if (now < start) return 0;
    if (now > end) return 100;

    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  }, [startTime, endTime]);

  return <div className={`font-mono`}>{timeLeft}</div>;
};

CountdownText.propTypes = {
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]).isRequired,
  allowedMinutes: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
};

export default CountdownText;
